import cn from 'classnames'
import {AnimatePresence, m, Variants} from 'framer-motion'
import _ from 'lodash'
import {FC, useEffect, useRef} from 'react'

import {CloseIcon} from '@/components/common/icons/CloseIcon'
import Overlay from '@/components/common/overlay/Overlay'
import {MODAL_TRANSITION} from '@/theme/cssTransitions'
import {IconSize} from '@/theme/icons'
import {CONTAINER_PADDING_MOBILE, MODAL_HEADER_HEIGHT} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {useUniqueClassName} from '@/utils/hooks/useUniqueClassName'
import {
  MOBILE_SCREENSIZE,
  TABLET_SCREENSIZE,
  USER_TABLET_SCREENSIZE,
} from '@/utils/styles/breakpoints'

export type ModalProps = {
  isVisible: boolean
  closeModal?: (e?: any) => void
  isRounded?: boolean
  hasPadding?: boolean
  hasCloseIcon?: boolean
  isFullWidth?: boolean
  isMobileFullScreen?: boolean
  isMobileCloseIconSticky?: boolean
  hasCustomScrollbar?: boolean
  preventCloseOnClickOutside?: boolean
  onExitComplete?: () => void
}

export const modalVariants: Variants = {
  hidden: {
    scale: 0.85,
    opacity: 0,
    transition: {duration: MODAL_TRANSITION, ease: [0, 0, 0.58, 1]},
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {duration: MODAL_TRANSITION, ease: [0, 0, 0.58, 1], delay: 0.16},
  },
}

export const Modal: FC<React.PropsWithChildren<ModalProps>> = ({
  isVisible,
  closeModal,
  isRounded = false,
  hasPadding = true,
  hasCloseIcon = true,
  isFullWidth = false,
  isMobileFullScreen = false,
  isMobileCloseIconSticky = false,
  hasCustomScrollbar = false,
  preventCloseOnClickOutside = false,
  onExitComplete,
  children,
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isVisible) {
      modalRef.current?.focus()
    }
  }, [isVisible])

  const className = useUniqueClassName('motion-modal')

  return (
    <>
      <AnimatePresence onExitComplete={onExitComplete}>
        {isVisible && (
          <div
            className={cn(
              `${className}-wrapper`,
              isFullWidth && 'full-width',
              isMobileFullScreen && 'full-screen',
            )}
          >
            <Overlay
              close={preventCloseOnClickOutside ? undefined : closeModal}
              visible={isVisible}
            />
            <m.div
              className={`${className}-content`}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              onUpdate={_.noop}
            >
              {/* A dialog closing on Escape is the expected pattern, the rule doesn't know it */}
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
              <div
                data-test="Modal-closeModal"
                // 'modal-stable' is a fixed class name (unlike the per-instance `className` above) so
                // external CSS can detect an open modal via :has(.modal-stable)
                className={cn(
                  className,
                  'modal-stable',
                  isRounded && 'rounded',
                  hasPadding && 'with-padding',
                )}
                onKeyDown={(e) => e.key === 'Escape' && closeModal?.(e)}
                role="dialog"
                aria-modal="true"
                tabIndex={-1}
                ref={modalRef}
              >
                {hasCloseIcon && (
                  <div
                    className={cn(
                      `${className}-close-wrapper`,
                      isMobileCloseIconSticky && 'sticky',
                    )}
                  >
                    <button
                      data-test="Modal-closeButton"
                      onClick={closeModal}
                      className={`${className}-close-button`}
                      type="button"
                    >
                      <CloseIcon size={IconSize.medium} />
                    </button>
                  </div>
                )}
                {hasCustomScrollbar ? children : <div>{children}</div>}
              </div>
            </m.div>
          </div>
        )}
      </AnimatePresence>

      <style jsx global>{`
        .${className}-wrapper {
          width: 100%;
          height: 100%;
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 4;
          cursor: default;
        }
        .${className}-wrapper .motion-overlay {
          background-color: ${theme.palette.overlay.main};
        }
        .${className}-content {
          display: flex;
          justify-content: center;
          max-width: calc(100% - ${theme.spacing[6]} * 2);
          max-height: calc(100vh - ${theme.spacing[5]} * 2);
          max-height: calc(100svh - ${theme.spacing[5]} * 2);
          z-index: 2;
        }
        .${className} {
          position: relative;
          background-color: ${theme.palette.background.default};
          border-radius: 12px;
          overflow-y: auto;
          overflow-x: hidden;
        }
        .${className}:focus-visible {
          outline: none;
        }
        .${className}.with-padding {
          padding: ${theme.spacing[6]};
        }
        .${className}-close-wrapper {
          position: absolute;
          top: 0;
          right: 0;
          z-index: 2;
        }
        .${className}-close-button {
          padding: ${theme.spacing[3]};
        }
        .${className}-wrapper {
          ::-webkit-scrollbar-track,
          ::-webkit-scrollbar,
          ::-webkit-scrollbar-thumb {
            width: 4px;
          }
          ::-webkit-scrollbar-track,
          ::-webkit-scrollbar {
            background-color: transparent;
          }
          ::-webkit-scrollbar-thumb {
            border-radius: 8px;
            background-color: ${theme.palette.gray[400]};
          }
        }
        .${className}-wrapper.full-width .${className}-content {
          width: 100%;
          height: 100%;
          max-width: calc(100% - ${theme.spacing[4]} * 2);
        }
        .${className}-wrapper.full-width .${className} {
          width: 100%;
        }
        @media ${TABLET_SCREENSIZE} {
          .${className}-wrapper .${className}-content {
            max-width: calc(100% - ${theme.spacing[5]} * 2);
          }
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .${className}-wrapper.full-screen {
            z-index: 4;
          }
          .${className}-wrapper.full-screen .${className}-content {
            margin: 0;
            height: 100%;
            width: 100%;
            max-width: inherit;
            max-height: inherit;
          }
          .${className}-wrapper.full-screen .${className} {
            width: 100%;
            border-radius: 0;
          }
          .${className}-wrapper.full-width .${className}-content {
            max-width: calc(100% - ${theme.spacing[3]} * 2);
          }
          .${className}-close-wrapper.sticky {
            position: sticky;
            top: 0;
            right: 0;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            height: ${MODAL_HEADER_HEIGHT};
            background-color: ${theme.palette.background.default};
            box-shadow: inset 0 -1px 0 ${theme.palette.gray[200]};
            z-index: 2;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .${className}-content {
            width: 100%;
            margin: ${CONTAINER_PADDING_MOBILE};
            max-width: inherit;
          }
          .${className} {
            width: 100%;
          }
          .${className}.with-padding {
            padding: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
